import { useState } from 'react';
import React from 'react';
import { musicApi } from '@/services/api';
import { 
  Type, Layout, Clock, Music, Maximize, Image, Tag, 
  Settings2, ChevronDown, ChevronRight, Sliders,
  AlignLeft, AlignCenter, AlignRight, Subtitles, Wand2,
  Upload, Trash2, Play, Pause, Zap, RefreshCw,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ASPECT_RATIOS,
  CAPTION_STYLES,
  CAPTION_LANGUAGES,
  CAPTION_FONTS,
  BACKGROUND_TYPES,
  SPEAKER_LAYOUTS,
  EXPORT_QUALITIES,
  MODULE_FIELDS,
} from './editorConfig';

/**
 * VideoEditorSidebar
 * 
 * Contains all editing controls organized in collapsible sections.
 * Shows only relevant fields based on the module type.
 */
export const VideoEditorSidebar = ({ 
  settings, 
  onSettingsChange,
  moduleType = 'full',
  trialActive = false,
  selectedTextTrack = null,
  onTextTracksChange,
  onAutoSubtitles,
  activeBrandId = null,
  onAddMusicToTimeline = null,   // (track: {id, name, url, duration_seconds}) => void
  className = '',
  style = {}
}) => {
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    format: true,
    timing: false,
    text: true,
    audio: false,
    visual: false,
    branding: false,
    music: moduleType === 'reels',
  });

  // Music library state (reels only)
  const [musicLibrary, setMusicLibrary] = useState([]);
  const [musicLoading, setMusicLoading] = useState(false);
  const [activeTrackId, setActiveTrackId] = useState(null);   // selected in timeline
  const [previewingId, setPreviewingId] = useState(null);     // currently previewing
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const musicFileRef = React.useRef(null);
  const previewAudioRef = React.useRef(null);                  // real audio for preview

  const loadMusic = React.useCallback(async () => {
    if (!activeBrandId) return;
    setMusicLoading(true);
    try {
      setMusicLibrary(await musicApi.list(activeBrandId));
    } catch { /* silent */ } finally {
      setMusicLoading(false);
    }
  }, [activeBrandId]);

  React.useEffect(() => {
    if (moduleType === 'reels' && activeBrandId) loadMusic();
  }, [moduleType, activeBrandId, loadMusic]);

  const handleMusicUpload = async (file) => {
    if (!activeBrandId) return;
    setUploadingMusic(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('brand_id', activeBrandId);
      form.append('name', file.name.replace(/\.[^.]+$/, ''));
      await musicApi.upload(form);
      await loadMusic();
    } catch { /* silent */ } finally {
      setUploadingMusic(false);
    }
  };

  const handleSuggestMusic = async () => {
    if (!activeBrandId || musicLibrary.length === 0) return;
    try {
      const data = await musicApi.suggest({ brand_id: activeBrandId, clip_keywords: [], mood: 'energetic' });
      if (data.suggested_id) {
        const suggested = musicLibrary.find(t => t.id === data.suggested_id);
        if (suggested) handleAddToTimeline(suggested);
      }
    } catch { /* silent */ }
  };

  // ── Audio preview ────────────────────────────────────────────────────────────
  const handlePreview = (track) => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (previewingId === track.id) {
      // Toggle off
      audio.pause();
      audio.currentTime = 0;
      setPreviewingId(null);
    } else {
      // Play this track
      audio.src = track.url;
      audio.volume = 0.8;
      audio.play().then(() => setPreviewingId(track.id)).catch(() => {});
    }
  };

  // ── Add to timeline ───────────────────────────────────────────────────────────
  const handleAddToTimeline = (track) => {
    setActiveTrackId(track.id);
    // Update settings with music URL (for preview panel sync)
    onSettingsChange({ ...settings, musicTrackId: track.id, musicUrl: track.url });
    // Add to timeline via callback
    onAddMusicToTimeline?.(track);
  };

  // Get visible fields for this module
  const visibleFields = MODULE_FIELDS[moduleType] || MODULE_FIELDS.full;
  const isFieldVisible = (field) => visibleFields.includes(field);

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Update a single setting
  const updateSetting = (key, value) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  // Section header component
  const SectionHeader = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-3 hover:bg-surface-hover rounded-lg"
    >
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color: 'hsl(var(--primary))' }} />
        <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
          {label}
        </span>
      </div>
      {expandedSections[id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
    </button>
  );

  return (
    <div className={`overflow-y-auto ${className}`} style={{ background: 'hsl(var(--surface))' }}>
      {/* ── Text Track Editor — shows when a text/subtitle clip is selected in timeline ── */}
      {selectedTextTrack && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="px-3 py-2 flex items-center gap-2" style={{ background: 'hsl(var(--primary) / 0.08)' }}>
            <Type size={14} style={{ color: 'hsl(var(--primary))' }} />
            <span className="text-xs font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              {selectedTextTrack.isSubtitle ? 'Subtítulo seleccionado' : 'Texto seleccionado'}
            </span>
          </div>
          <div className="px-3 py-3 space-y-3">
            {/* Text content */}
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Texto</label>
              <Input
                value={selectedTextTrack.text || ''}
                onChange={e => onTextTracksChange?.(prev => prev.map(t => t.id === selectedTextTrack.id ? { ...t, text: e.target.value } : t))}
                onFocus={e => e.target.select()}
                className="h-8 text-sm"
                placeholder="Escribe tu texto..."
              />
            </div>
            {/* Font */}
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Fuente</label>
              <Select value={selectedTextTrack.fontFamily || 'default'} onValueChange={v => onTextTracksChange?.(prev => prev.map(t => t.id === selectedTextTrack.id ? { ...t, fontFamily: v } : t))}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAPTION_FONTS.map(f => <SelectItem key={f.id} value={f.id}><span style={{ fontFamily: f.family }}>{f.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Font size + color */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Tamaño</label>
                <Select value={String(selectedTextTrack.fontSize || 18)} onValueChange={v => onTextTracksChange?.(prev => prev.map(t => t.id === selectedTextTrack.id ? { ...t, fontSize: parseInt(v) } : t))}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 72].map(s => <SelectItem key={s} value={String(s)}>{s}px</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={selectedTextTrack.color || '#ffffff'}
                    onChange={e => onTextTracksChange?.(prev => prev.map(t => t.id === selectedTextTrack.id ? { ...t, color: e.target.value } : t))}
                    className="h-8 w-10 rounded cursor-pointer border-0 p-0" style={{ background: 'none' }}
                  />
                  <span className="text-xs font-mono" style={{ color: 'hsl(var(--foreground-muted))' }}>{selectedTextTrack.color || '#fff'}</span>
                </div>
              </div>
            </div>
            {/* Alignment */}
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Alineación del texto</label>
              <div className="flex gap-1">
                {[
                  { id: 'left', icon: AlignLeft },
                  { id: 'center', icon: AlignCenter },
                  { id: 'right', icon: AlignRight },
                ].map(({ id, icon: Icon }) => (
                  <button key={id}
                    onClick={() => onTextTracksChange?.(prev => prev.map(t => t.id === selectedTextTrack.id ? { ...t, textAlign: id } : t))}
                    className="flex-1 py-1.5 rounded flex items-center justify-center transition-colors"
                    style={{
                      background: (selectedTextTrack.textAlign === id || (!selectedTextTrack.textAlign && id === 'center')) ? 'hsl(var(--primary))' : 'hsl(var(--surface-elevated))',
                      border: '1px solid hsl(var(--border))',
                    }}
                  >
                    <Icon size={14} style={{ color: (selectedTextTrack.textAlign === id || (!selectedTextTrack.textAlign && id === 'center')) ? '#fff' : 'hsl(var(--foreground-muted))' }} />
                  </button>
                ))}
              </div>
            </div>
            {/* Position on canvas */}
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Posición en pantalla</label>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { label: '↖', x: 10, y: 12 }, { label: '↑', x: 50, y: 12 }, { label: '↗', x: 90, y: 12 },
                  { label: '←', x: 10, y: 50 }, { label: '●', x: 50, y: 50 }, { label: '→', x: 90, y: 50 },
                  { label: '↙', x: 10, y: 88 }, { label: '↓', x: 50, y: 88 }, { label: '↘', x: 90, y: 88 },
                ].map(({ label, x, y }) => {
                  const curX = selectedTextTrack.position?.x ?? 50;
                  const curY = selectedTextTrack.position?.y ?? 50;
                  const isActive = Math.abs(curX - x) < 8 && Math.abs(curY - y) < 8;
                  return (
                    <button key={`${x}-${y}`}
                      onClick={() => onTextTracksChange?.(prev => prev.map(t => t.id === selectedTextTrack.id ? { ...t, position: { x, y } } : t))}
                      className="py-1.5 rounded flex items-center justify-center text-sm transition-colors"
                      style={{
                        background: isActive ? 'hsl(var(--primary))' : 'hsl(var(--surface-elevated))',
                        border: '1px solid hsl(var(--border))',
                        color: isActive ? '#fff' : 'hsl(var(--foreground-muted))',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Basic Section — thumbnail only (title moved to header) */}
      {isFieldVisible('thumbnail') && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <SectionHeader id="basic" icon={Type} label="Basic" />
          {expandedSections.basic && (
            <div className="px-3 pb-3 space-y-4">
              {isFieldVisible('thumbnail') && (
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Thumbnail
                  </label>
                  <div 
                    className="aspect-video rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ background: 'hsl(var(--surface-elevated))', border: '1px dashed hsl(var(--border))' }}
                  >
                    <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                      Click to upload
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Format Section */}
      {(isFieldVisible('aspectRatio') || isFieldVisible('exportQuality')) && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <SectionHeader id="format" icon={Layout} label="Format" />
          {expandedSections.format && (
            <div className="px-3 pb-3 space-y-4">
              {isFieldVisible('aspectRatio') && (
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Aspect Ratio
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {ASPECT_RATIOS.map(ar => (
                      <button
                        key={ar.id}
                        onClick={() => updateSetting('aspectRatio', ar.id)}
                        className={`p-2 rounded-lg text-left transition-colors ${settings.aspectRatio === ar.id ? 'ring-2 ring-primary' : ''}`}
                        style={{ 
                          background: settings.aspectRatio === ar.id ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--surface-elevated))',
                          border: '1px solid hsl(var(--border))'
                        }}
                      >
                        <p className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>{ar.label}</p>
                        <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{ar.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {isFieldVisible('exportQuality') && (
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Export Quality
                  </label>
                  <Select value={settings.exportQuality} onValueChange={(val) => updateSetting('exportQuality', val)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPORT_QUALITIES.map(q => (
                        <SelectItem key={q.id} value={q.id} disabled={q.premium && trialActive}>
                          <div className="flex items-center gap-2">
                            <span>{q.label}</span>
                            <span className="text-xs opacity-60">{q.desc}</span>
                            {q.premium && trialActive && (
                              <span className="text-xs px-1 py-0.5 rounded" style={{ background: 'hsl(var(--warning))', color: '#000' }}>
                                PRO
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timing Section — hidden per user request */}

      {/* Text Section (Captions, Hook, CTA) */}
      {(isFieldVisible('captions') || isFieldVisible('hook') || isFieldVisible('cta')) && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <SectionHeader id="text" icon={Type} label="Text & Captions" />
          {expandedSections.text && (
            <div className="px-3 pb-3 space-y-4">
              {/* Captions */}
              {isFieldVisible('captions') && (
                <>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium" style={{ color: 'hsl(var(--foreground-muted))' }}>
                      Captions
                    </label>
                    <Switch 
                      checked={settings.captionsEnabled} 
                      onCheckedChange={(val) => updateSetting('captionsEnabled', val)} 
                    />
                  </div>
                  {/* Generate subtitles button — always visible in captions section */}
                  <button
                    data-testid="generate-subtitles-btn"
                    onClick={onAutoSubtitles}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90 active:scale-95"
                    style={{
                      background: 'hsl(var(--primary))',
                      color: 'hsl(var(--primary-foreground))',
                    }}
                  >
                    <Wand2 size={13} />
                    Generar subtítulos ahora
                  </button>
                  {settings.captionsEnabled && (
                    <>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                          Style
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {CAPTION_STYLES.slice(0, 4).map(style => (
                            <button
                              key={style.id}
                              onClick={() => updateSetting('captionStyle', style.id)}
                              className={`p-2 rounded-lg text-left transition-colors ${settings.captionStyle === style.id ? 'ring-2 ring-primary' : ''}`}
                              style={{ 
                                background: settings.captionStyle === style.id ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--surface-elevated))',
                              }}
                            >
                              <p className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>{style.label}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                          Language
                        </label>
                        <Select value={settings.captionLanguage} onValueChange={(val) => updateSetting('captionLanguage', val)}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CAPTION_LANGUAGES.map(lang => (
                              <SelectItem key={lang.id} value={lang.id}>{lang.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                          Font
                        </label>
                        <Select value={settings.captionFont || 'default'} onValueChange={(val) => updateSetting('captionFont', val)}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CAPTION_FONTS.map(f => (
                              <SelectItem key={f.id} value={f.id}>
                                <span style={{ fontFamily: f.family }}>{f.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                          Color
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={settings.captionColor || '#ffffff'}
                            onChange={(e) => updateSetting('captionColor', e.target.value)}
                            className="h-8 w-10 rounded cursor-pointer border-0 p-0"
                            style={{ background: 'none' }}
                          />
                          <span className="text-xs font-mono" style={{ color: 'hsl(var(--foreground-muted))' }}>
                            {settings.captionColor || '#ffffff'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                          Position
                        </label>
                        <div className="flex gap-2">
                          {['top', 'center', 'bottom'].map(pos => (
                            <button
                              key={pos}
                              onClick={() => updateSetting('captionPosition', pos)}
                              className={`flex-1 py-1.5 rounded text-xs capitalize ${settings.captionPosition === pos ? 'ring-2 ring-primary' : ''}`}
                              style={{ background: 'hsl(var(--surface-elevated))' }}
                            >
                              {pos}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Hook */}
              {isFieldVisible('hook') && (
                <div className="pt-2 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Hook Text
                  </label>
                  <Input
                    value={settings.hookText}
                    onChange={(e) => updateSetting('hookText', e.target.value)}
                    placeholder="Add a hook text..."
                    className="h-9"
                  />
                  {settings.hookText && (
                    <div className="mt-2 flex gap-2">
                      {['top', 'center'].map(pos => (
                        <button
                          key={pos}
                          onClick={() => updateSetting('hookPosition', pos)}
                          className={`flex-1 py-1.5 rounded text-xs capitalize ${settings.hookPosition === pos ? 'ring-2 ring-primary' : ''}`}
                          style={{ background: 'hsl(var(--surface-elevated))' }}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* CTA */}
              {isFieldVisible('cta') && (
                <div className="pt-2 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Call to Action
                  </label>
                  <Input
                    value={settings.ctaText}
                    onChange={(e) => updateSetting('ctaText', e.target.value)}
                    placeholder="Follow for more!"
                    className="h-9"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Audio Section */}
      {isFieldVisible('music') && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <SectionHeader id="audio" icon={Music} label="Audio" />
          {expandedSections.audio && (
            <div className="px-3 pb-3 space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Background Music
                </label>
                <Switch 
                  checked={settings.musicEnabled} 
                  onCheckedChange={(val) => updateSetting('musicEnabled', val)} 
                />
              </div>
              {settings.musicEnabled && (
                <>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                      Music Volume: {settings.musicVolume}%
                    </label>
                    <Slider
                      value={[settings.musicVolume]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={([val]) => updateSetting('musicVolume', val)}
                    />
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    Select Music Track
                  </Button>
                </>
              )}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Original Audio: {settings.originalAudioVolume}%
                </label>
                <Slider
                  value={[settings.originalAudioVolume]}
                  min={0}
                  max={100}
                  step={5}
                  onValueChange={([val]) => updateSetting('originalAudioVolume', val)}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Visual Section */}
      {(isFieldVisible('zoom') || isFieldVisible('speakerLayout') || isFieldVisible('background')) && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <SectionHeader id="visual" icon={Maximize} label="Visual" />
          {expandedSections.visual && (
            <div className="px-3 pb-3 space-y-4">
              {/* Zoom */}
              {isFieldVisible('zoom') && (
                <>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium" style={{ color: 'hsl(var(--foreground-muted))' }}>
                      Zoom / Punch-in
                    </label>
                    <Switch 
                      checked={settings.zoomEnabled} 
                      onCheckedChange={(val) => updateSetting('zoomEnabled', val)} 
                    />
                  </div>
                  {settings.zoomEnabled && (
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                        Zoom Level: {settings.zoom.toFixed(1)}x
                      </label>
                      <Slider
                        value={[settings.zoom]}
                        min={1}
                        max={2}
                        step={0.1}
                        onValueChange={([val]) => updateSetting('zoom', val)}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Speaker Layout */}
              {isFieldVisible('speakerLayout') && (
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Speaker Layout
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {SPEAKER_LAYOUTS.map(layout => (
                      <button
                        key={layout.id}
                        onClick={() => updateSetting('speakerLayout', layout.id)}
                        className={`p-2 rounded-lg text-left transition-colors ${settings.speakerLayout === layout.id ? 'ring-2 ring-primary' : ''}`}
                        style={{ 
                          background: settings.speakerLayout === layout.id ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--surface-elevated))',
                        }}
                      >
                        <p className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>{layout.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Background */}
              {isFieldVisible('background') && (
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                    Background
                  </label>
                  <Select value={settings.background} onValueChange={(val) => updateSetting('background', val)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BACKGROUND_TYPES.map(bg => (
                        <SelectItem key={bg.id} value={bg.id}>
                          <div>
                            <span>{bg.label}</span>
                            <span className="text-xs opacity-60 ml-2">{bg.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {settings.background === 'solid' && (
                    <div className="mt-2">
                      <Input
                        type="color"
                        value={settings.backgroundColor}
                        onChange={(e) => updateSetting('backgroundColor', e.target.value)}
                        className="h-9 w-full"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Branding Section */}
      {isFieldVisible('branding') && (
        <div className="border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <SectionHeader id="branding" icon={Tag} label="Branding" />
          {expandedSections.branding && (
            <div className="px-3 pb-3 space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Watermark
                </label>
                <Switch 
                  checked={settings.brandingEnabled} 
                  onCheckedChange={(val) => updateSetting('brandingEnabled', val)} 
                />
              </div>
              {settings.brandingEnabled && (
                <>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                      Position
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(pos => (
                        <button
                          key={pos}
                          onClick={() => updateSetting('watermarkPosition', pos)}
                          className={`py-1.5 rounded text-xs ${settings.watermarkPosition === pos ? 'ring-2 ring-primary' : ''}`}
                          style={{ background: 'hsl(var(--surface-elevated))' }}
                        >
                          {pos.replace('-', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                      Opacity: {settings.watermarkOpacity}%
                    </label>
                    <Slider
                      value={[settings.watermarkOpacity]}
                      min={20}
                      max={100}
                      step={10}
                      onValueChange={([val]) => updateSetting('watermarkOpacity', val)}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {/* ── MUSIC LIBRARY (reels only) ── */}
      {moduleType === 'reels' && (
        <div className="rounded-xl overflow-hidden mb-2" style={{ background: 'hsl(var(--surface))' }}>
          {/* Hidden audio element for real playback preview */}
          <audio ref={previewAudioRef} onEnded={() => setPreviewingId(null)} />

          <button onClick={() => toggleSection('music')}
            className="w-full flex items-center justify-between p-3 hover:bg-surface-hover rounded-lg">
            <div className="flex items-center gap-2">
              <Music size={16} style={{ color: 'hsl(var(--primary))' }} />
              <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>Música</span>
              {activeTrackId && (
                <span className="w-2 h-2 rounded-full" style={{ background: 'hsl(var(--primary))' }} />
              )}
            </div>
            {expandedSections.music ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {expandedSections.music && (
            <div className="px-3 pb-3 space-y-2">
              {/* Upload + IA suggest row */}
              <div className="flex gap-2">
                <input ref={musicFileRef} type="file" accept=".mp3,.m4a,.wav,.aac" className="hidden"
                  onChange={e => e.target.files[0] && handleMusicUpload(e.target.files[0])} />
                <Button size="sm" variant="outline" className="flex-1 gap-1 text-xs"
                  onClick={() => musicFileRef.current?.click()} disabled={uploadingMusic}>
                  {uploadingMusic ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                  Subir MP3
                </Button>
                {musicLibrary.length > 0 && (
                  <Button size="sm" variant="outline" className="gap-1 text-xs"
                    onClick={handleSuggestMusic} title="IA sugiere la mejor canción">
                    <Zap size={12} style={{ color: 'hsl(var(--primary))' }} /> IA
                  </Button>
                )}
              </div>

              {/* Track list */}
              {musicLoading ? (
                <div className="flex justify-center py-3">
                  <RefreshCw size={16} className="animate-spin" style={{ color: 'hsl(var(--foreground-subtle))' }} />
                </div>
              ) : musicLibrary.length === 0 ? (
                <p className="text-xs text-center py-3" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  Sube un MP3 para añadir música al reel
                </p>
              ) : (
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {musicLibrary.map(track => {
                    const isAdded   = activeTrackId === track.id;
                    const isPreviewing = previewingId === track.id;
                    return (
                      <div key={track.id} data-testid={`music-track-${track.id}`}
                        className="flex items-center gap-2 px-2 py-2 rounded-lg"
                        style={{
                          background: isAdded ? 'hsl(var(--primary)/0.12)' : 'hsl(var(--surface-elevated))',
                          border: isAdded ? '1px solid hsl(var(--primary)/0.4)' : '1px solid transparent',
                        }}>

                        {/* Preview play/pause */}
                        <button data-testid={`preview-track-${track.id}`}
                          onClick={() => handlePreview(track)}
                          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 transition-colors"
                          style={{ background: isPreviewing ? 'hsl(var(--primary))' : 'hsl(var(--surface))' }}>
                          {isPreviewing
                            ? <Pause size={12} color={isPreviewing ? '#000' : undefined} style={{ color: isPreviewing ? '#000' : 'hsl(var(--foreground-muted))' }} />
                            : <Play size={12} style={{ color: 'hsl(var(--foreground-muted))' }} />
                          }
                        </button>

                        {/* Track info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>{track.name}</p>
                          {track.duration_seconds > 0 && (
                            <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                              {Math.floor(track.duration_seconds / 60)}:{String(Math.floor(track.duration_seconds % 60)).padStart(2, '0')}
                            </p>
                          )}
                        </div>

                        {/* Add to timeline */}
                        {!isAdded ? (
                          <button data-testid={`add-track-${track.id}`}
                            onClick={() => handleAddToTimeline(track)}
                            title="Añadir al timeline"
                            className="px-2 py-0.5 rounded text-xs font-semibold transition-colors hover:opacity-80"
                            style={{ background: 'hsl(var(--primary))', color: '#000' }}>
                            +
                          </button>
                        ) : (
                          <button data-testid={`remove-track-${track.id}`}
                            onClick={() => { setActiveTrackId(null); onSettingsChange({ ...settings, musicTrackId: null, musicUrl: null }); onAddMusicToTimeline?.(null); }}
                            title="Quitar del timeline"
                            className="px-2 py-0.5 rounded text-xs"
                            style={{ background: 'hsl(var(--destructive)/0.2)', color: 'hsl(var(--destructive))' }}>
                            ✕
                          </button>
                        )}

                        {/* Delete from library */}
                        <button onClick={async e => { e.stopPropagation(); await musicApi.delete(track.id); loadMusic(); }}
                          className="p-1 rounded hover:opacity-70 flex-shrink-0">
                          <Trash2 size={11} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default VideoEditorSidebar;
